/* eslint-disable no-param-reassign */
import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';

import DefaultApiLimit from '../../../config/defaultLimit';
import { filterType } from '../../../config/enum';
import { ApiRequest } from '../../../interface/common.interface';
import {
  CommonFilterProperties,
  FilterUpdateProperties,
} from '../../../interface/filter.interface';
import {
  ClearFilters,
  RemoveSingleFilterFromList,
  ReplaceValueInFilter,
  UpdateCommonFilter,
} from '../../../shared-functions/CommonFilters';
import getErrorMessage from '../../../shared-functions/getErrorMessage';
import ocpiApi from '../../../utils/api/ocpi';

export interface OcpiBusinessDetailsInfo {
  name?: string;
  website?: string | null;
  logo?: any;
}

export interface OcpiRoleInfo {
  role: string;
  business_details?: OcpiBusinessDetailsInfo;
  country_code?: string;
  party_id?: string;
}

export interface OcpiEndpointInfo {
  identifier: string;
  role: string;
  url: string;
}

export interface OcpiVersionInfo {
  version: string;
  url: string;
}

export interface OcpiTenantBusinessDetails {
  roles?: OcpiRoleInfo[];
  name?: string;
  website?: string | null;
  logo?: any;
  [key: string]: any;
}

export interface OcpiTenant {
  _id?: string;
  id?: string;
  active?: boolean;
  businessDetails?: OcpiTenantBusinessDetails;
  endpoints?: OcpiEndpointInfo[];
  roles?: OcpiRoleInfo[];
  versions?: OcpiVersionInfo[];
  countryCode: string;
  createdAt?: string;
  hasTokenA?: boolean;
  hasTokenB?: boolean;
  hasTokenC?: boolean;
  lastUpdated?: string;
  masterTenantId?: string;
  partyId: string;
  role: string;
  senderCountryCode?: string;
  senderPartyId?: string;
  senderRole?: string;
  status?: string;
  updatedAt?: string;
  versionsUrl?: string;
  __v?: number;
  [key: string]: any;
}

/**
 * Fetches all registered OCPI tenants (roles) for the platform.
 */
export const fetchOcpiRoles = createAsyncThunk<OcpiTenant[], void>(
  'ocpi-connections/fetchOcpiRoles',
  async (_, { dispatch, rejectWithValue }) => {
    try {
      const response = await ocpiApi.listTenants();
      const rawData = response.data || {};
      const nestedTenants = Array.isArray(rawData.tenants) ? rawData.tenants : [];
      return Array.isArray(rawData) ? rawData : nestedTenants;
    } catch (error: any) {
      dispatch({
        type: 'notification/setNotificationMessage',
        payload: {
          status: true,
          message: error?.response?.data?.message || 'Failed to fetch roles',
          type: 'error',
        },
      });
      return rejectWithValue(error?.response?.data);
    }
  },
);

interface OcpiConnectionsResponse {
  results: any[];
  limit: number;
  page: number;
  totalResults: number;
  totalPages: number;
}

/**
 * Fetches all registered OCPI connections for the platform. The backend endpoint has no
 * pagination/sort support (it returns the full list), so page/limit/sort metadata is
 * derived client-side from the request so CustomTable's pagination controls stay wired.
 */
export const fetchOcpiConnections = createAsyncThunk<OcpiConnectionsResponse, ApiRequest | void>(
  'ocpi-connections/fetchOcpiConnections',
  async (data, { dispatch, rejectWithValue }) => {
    try {
      const response = await ocpiApi.listConnections(data?.role || '', data?.tenantId || '');
      const results = response.data || [];
      const limit = data?.limit || DefaultApiLimit.limit;
      const page = data?.page || DefaultApiLimit.page;
      return {
        results,
        limit,
        page,
        totalResults: results.length,
        totalPages: Math.max(1, Math.ceil(results.length / limit)),
      };
    } catch (error: any) {
      dispatch({
        type: 'notification/setNotificationMessage',
        payload: {
          status: true,
          message: error?.response?.data?.message || 'Failed to fetch connections',
          type: 'error',
        },
      });
      return rejectWithValue(error?.response?.data);
    }
  },
);

const connectionFilters: CommonFilterProperties[] = [
  {
    name: 'searchPlaceholder',
    key: 'searchBy',
    type: filterType.TEXT,
    value: '',
    applyFilter: false,
    enable: true,
    searchBy: true,
  },
  {
    name: 'Status',
    key: 'status',
    type: filterType.SELECT_FIELD,
    value: '',
    listOfValue: [
      { label: 'Pending', value: 'PENDING' },
      { label: 'Connected', value: 'CONNECTED' },
    ],
    applyFilter: false,
    enable: true,
  },
];

/**
 * Dispatches a notification message using the shared notification slice's action type.
 * Avoids a circular import on the notification slice from within this one.
 * @param {any} dispatch - The thunk dispatch function.
 * @param {any} message - The message to show.
 * @param {any} type - The notification type.
 */
const notify = (dispatch: any, message: string, type: 'success' | 'error') => {
  dispatch({
    type: 'notification/setNotificationMessage',
    payload: { status: true, message, type },
  });
};

/**
 * Creates a new OCPI tenant (role) and refreshes the tenant list on success.
 */
export const createOcpiTenant = createAsyncThunk<void, Record<string, any>>(
  'ocpi-connections/createOcpiTenant',
  async (data, { dispatch, rejectWithValue }) => {
    try {
      await ocpiApi.createTenant(data);
      notify(dispatch, 'Role added successfully', 'success');
      await dispatch(fetchOcpiRoles());
      return undefined;
    } catch (error: any) {
      const errorMessage = getErrorMessage(error, 'Failed to add role');
      notify(dispatch, errorMessage, 'error');
      return rejectWithValue(errorMessage);
    }
  },
);

/**
 * Deletes an OCPI tenant (role) and refreshes the tenant list on success.
 */
export const deleteOcpiTenant = createAsyncThunk<void, string>(
  'ocpi-connections/deleteOcpiTenant',
  async (id, { dispatch }) => {
    try {
      await ocpiApi.deleteTenant(id);
      notify(dispatch, 'Role deleted successfully', 'success');
      await dispatch(fetchOcpiRoles());
    } catch (error: any) {
      notify(dispatch, error?.response?.data?.message || 'Failed to delete role', 'error');
    }
  },
);

interface DeleteOcpiConnectionParameters {
  role: string;
  counterPartyId: string;
  counterCountryCode: string;
  counterRole: string;
}

/**
 * Deletes an OCPI connection (unregisters as sender) and refreshes the connection list on success.
 */
export const deleteOcpiConnection = createAsyncThunk<void, DeleteOcpiConnectionParameters>(
  'ocpi-connections/deleteOcpiConnection',
  async ({ role, counterPartyId, counterCountryCode, counterRole }, { dispatch }) => {
    try {
      await ocpiApi.deleteConnection(role, { counterPartyId, counterCountryCode, counterRole });
      notify(dispatch, 'Connection deleted successfully', 'success');
      await dispatch(fetchOcpiConnections({ role }));
    } catch (error: any) {
      notify(dispatch, error?.response?.data?.message || 'Failed to delete connection', 'error');
    }
  },
);

/**
 * Authorizes a pending OCPI connection (receiver role) and refreshes the connection list.
 */
export const authorizeOcpiConnection = createAsyncThunk<void, string>(
  'ocpi-connections/authorizeOcpiConnection',
  async (id, { dispatch }) => {
    try {
      await ocpiApi.authorizeConnection(id);
      notify(dispatch, 'Connection authorized and activated!', 'success');
      await dispatch(fetchOcpiConnections());
    } catch (error: any) {
      notify(dispatch, error?.response?.data?.message || 'Failed to authorize connection', 'error');
    }
  },
);

/**
 * Resolves the filter list for the connections table. Filter options are known upfront
 * (there is no backend filter-list endpoint for OCPI), so this only exists to satisfy
 * CustomTable's filterListToDispatch contract and is a no-op once filters are populated.
 */
export const fetchOcpiConnectionFilters = createAsyncThunk<CommonFilterProperties[], void>(
  'ocpi-connections/fetchOcpiConnectionFilters',
  async () => connectionFilters,
);

export interface OcpiConnectionsInitialState {
  tenants: OcpiTenant[];
  connections: any[];
  selectedRole: OcpiTenant | null;
  loadingRoles: boolean;
  loadingConns: boolean;
  filters: CommonFilterProperties[];
  filterListLoading: boolean;
  isFilterUpdated: boolean;
  limit: number;
  page: number;
  totalPages: number;
  totalResults: number;
  sortBy: string;
  sortField: string;
}

const initialState: OcpiConnectionsInitialState = {
  tenants: [],
  connections: [],
  selectedRole: null,
  loadingRoles: true,
  loadingConns: false,
  filters: connectionFilters,
  filterListLoading: false,
  isFilterUpdated: false,
  limit: DefaultApiLimit.limit,
  page: DefaultApiLimit.page,
  totalPages: 0,
  totalResults: 0,
  sortBy: '',
  sortField: '',
};

export const ocpiConnectionsSlice = createSlice({
  name: 'ocpi-connections',
  initialState,
  reducers: {
    /**
     * Sets the currently selected platform role.
     * @param {any} state - The current state.
     * @param {any} action - The action object.
     */
    setSelectedRole: (state, action: PayloadAction<OcpiTenant | null>) => {
      state.selectedRole = action.payload;
    },
    /**
     * Updates the connections table filters.
     * @param {any} state - The current state.
     * @param {any} action - The action object.
     */
    updateConnectionFilters: (state, action: PayloadAction<FilterUpdateProperties>) => {
      const payload = { ...action.payload };
      if (payload.type === 'add') {
        const { updatedCommonFilter, isFilterUpdated } = UpdateCommonFilter(
          state.filters,
          action.payload,
        );
        state.filters = updatedCommonFilter;
        state.isFilterUpdated = isFilterUpdated;
      } else if (payload.type === 'remove') {
        state.filters = RemoveSingleFilterFromList(state.filters, payload.key);
        state.isFilterUpdated = true;
      } else if (payload.type === 'clear') {
        state.filters = ClearFilters(state.filters);
        state.isFilterUpdated = true;
      } else if (payload.type === 'replace') {
        const { updatedCommonFilter, isFilterUpdated } = ReplaceValueInFilter(
          state.filters,
          action.payload,
        );
        state.filters = updatedCommonFilter;
        state.isFilterUpdated = isFilterUpdated;
      }
    },
    /**
     * Clears the isFilterUpdated flag once the resulting fetch has been dispatched.
     * @param {any} state - The current state.
     */
    disableFiltersApplied: (state) => {
      state.isFilterUpdated = false;
    },
    /**
     * Sets the connections table's active sort field and direction.
     * @param {any} state - The current state.
     * @param {any} action - The action payload containing sortField and sortBy.
     */
    setSortData: (state, action: PayloadAction<{ sortField: string; sortBy: string }>) => {
      state.sortField = action.payload.sortField;
      state.sortBy = action.payload.sortBy;
    },
    /**
     * Resets the slice to its initial state.
     * @param {any} state - The current state.
     */
    resetOcpiConnectionsToInitialState: (state) => {
      Object.assign(state, initialState);
    },
  },
  /**
   * Extra reducers to handle thunk requests.
   * @param {any} builder - The builder object.
   */
  extraReducers: (builder) => {
    builder
      .addCase(fetchOcpiRoles.pending, (state) => {
        state.loadingRoles = true;
      })
      .addCase(fetchOcpiRoles.fulfilled, (state, action) => {
        state.loadingRoles = false;
        state.tenants = action.payload;
      })
      .addCase(fetchOcpiRoles.rejected, (state) => {
        state.loadingRoles = false;
      })
      .addCase(fetchOcpiConnections.pending, (state) => {
        state.loadingConns = true;
      })
      .addCase(fetchOcpiConnections.fulfilled, (state, action) => {
        state.loadingConns = false;
        state.connections = action.payload.results;
        state.limit = action.payload.limit;
        state.page = action.payload.page;
        state.totalPages = action.payload.totalPages;
        state.totalResults = action.payload.totalResults;
      })
      .addCase(fetchOcpiConnections.rejected, (state) => {
        state.loadingConns = false;
      })
      .addCase(fetchOcpiConnectionFilters.pending, (state) => {
        state.filterListLoading = true;
      })
      .addCase(fetchOcpiConnectionFilters.fulfilled, (state, action) => {
        state.filterListLoading = false;
        state.filters = action.payload;
      })
      .addCase(fetchOcpiConnectionFilters.rejected, (state) => {
        state.filterListLoading = false;
      });
  },
});

export const {
  setSelectedRole,
  updateConnectionFilters,
  disableFiltersApplied,
  setSortData,
  resetOcpiConnectionsToInitialState,
} = ocpiConnectionsSlice.actions;

export default ocpiConnectionsSlice.reducer;
